import axios from "axios";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";

interface AuthContextType {
  accessToken: string | null;
  login: (identity: string, password: string) => Promise<void>;
  logout: () => void;
  authData: any | null;
  join: (
    identity: string,
    password: string,
    userType: string
  ) => Promise<{ id: number; username: string }>;
  withdraw: (identity: string, password: string) => Promise<void>;
  isInit: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [authData, setAuthData] = useState<any | null>(null);

  const [isInit, setIsInit] = useState<boolean>(false);

  window.addEventListener("message",async (event) => {
 
    if (event.origin !== "https://smart.aonecustoms.com" && "https://www.aone-ivis.com" !== event.origin) return; 


    if (event.data.type === "TOKEN") {
      const token = event.data.token;
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/exchange_token`, { access_token: token });
      const { access_token, refresh_token: newRefreshToken } = response.data;
      localStorage.setItem("refresh_token", newRefreshToken);
      setAccessToken(access_token);
    }
  });

  const TokenRefrash = async () => {
    console.log("AuthProvider mounted");
    const storedRefreshToken = localStorage.getItem("refresh_token");

    if (storedRefreshToken) {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/refresh`,
        {
          refresh_token: storedRefreshToken,
        }
      );

      const { access_token, refresh_token: newRefreshToken } = response.data;
      setAccessToken(access_token);

      localStorage.setItem("refresh_token", newRefreshToken);
    } else {
    }
  };

  const isInitialized = useRef(false);

  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      (async () => {
        await TokenRefrash();
        setIsInit(true);
      })();
    }
  }, []);

  const login = async (identity: string, password: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/login`,
        {
          identity,
          password,
        }
      );

      setAccessToken(response.data.access_token);
      const refreshToken = response.data.refresh_token;
      localStorage.setItem("refresh_token", refreshToken);
    } catch (error) {
      throw error;
    }
  };

  const withdraw = async (identity: string, password: string) => {
    const response = await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/auth/withdraw`,
      {
        data: {
          identity,
          password,
        },
      }
    );
    return response.data;
  };

  useEffect(() => {
    if (!accessToken) {
      setAuthData(null);
      return;
    }

    const payload = JSON.parse(atob(accessToken.split(".")[1]));

    setAuthData({
      identity: payload.identity,
      id: payload.id,
      type: payload.type,
    });

    const expiryTime = payload.exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    if (timeUntilExpiry > 0) {
      setTimeout(() => {
        TokenRefrash();
      }, Math.max(timeUntilExpiry - 5 * 60 * 1000, 0));
    }
  }, [accessToken]);

  const redirectToLogin = () => {
    setAccessToken(null);
    localStorage.removeItem("refresh_token");
    setAuthData(null);

    if (
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/admin")
    ) {
      window.location.replace("/");
    }
  };

  const join = async (identity: string, password: string, userType: string) => {
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/auth/join`,
      {
        identity,
        password,
        userType,
      }
    );
    return response.data;
  };

  const logout = () => redirectToLogin();

  const value = {
    accessToken,
    login,
    logout,
    authData,
    join,
    withdraw,
    isInit,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
